// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import { extent, max, min } from 'd3-array';
import Zion from './utils/Zion';
import { linearAxis, timeAxis } from '../../../factory/axes';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import { stylePremium } from './utils/helpers';
import config from './config';
import graphic from './graphic';

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Deciembre',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

let chartData;
let rawData;

const margins = {
  top: 20,
  right: 110,
  bottom: 25,
  left: 45,
};

const parser = d3.timeParse('%Y-%m-%d');

// let recessionData;
let minValue;
let maxValue;

let minDomain;
let maxDomain;

let lastData;
let lastUpdatedStr;
let latestMonth;

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;
const pTime = d3.timeParse('%Y-%m-%d');

stylePremium(CNNPolitics, CNNBusiness);

const rParser = d3.timeParse('%Y-%m-%d');

const MESES_ES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

function formatDecimalES(num, decimals = 2) {
  return num.toFixed(decimals).replace('.', ',');
}

function formatDateSpanish(date, includeYear = true) {
  const d = date instanceof Date ? date : new Date(date);
  const base = `${d.getDate()} de ${MESES_ES[d.getMonth()]}`;
  return includeYear ? `${base} de ${d.getFullYear()}` : base;
}

function formatDateTimeSpanish(isoString) {
  const d = new Date(isoString);
  const dateStr = formatDateSpanish(d);
  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    hour12: true,
  });
  const timeStr = timeFormatter.format(d).replace(/([AP])M$/, (_, p) => `${p.toLowerCase()}.m.`);
  return `${dateStr} a las ${timeStr}, hora de Miami`;
}

let dekText;

d3.json('https://ix.cnn.io/data/gas-prices/aaa_gas_prices_avg_national_five_years.json').then((data) => {
  // Map and filter data, removing any invalid entries
  chartData = data.data
    .map(d => ({
      date: d3.timeParse('%Y-%m-%d')(d.date),
      value: +d.price,
    }))
    .filter(d => d.date && !isNaN(d.value) && d.value !== null);

  // Validate we have data to display
  if (chartData.length === 0) {
    console.error('No valid gas price data available');
    d3.select('#graphic').append('p')
      .style('text-align', 'center')
      .style('padding', '40px')
      .text('Data temporarily unavailable');
    return;
  }

  minDomain = d3.min(chartData, d => d.date);
  maxDomain = d3.max(chartData, d => d.date);

  minValue = d3.min(chartData, d => d.value) * 0.95;
  maxValue = d3.max(chartData, d => d.value) * 1.05;

  // Get the last data point
  const lastDataArray = chartData.filter(d => d.date.getTime() === maxDomain.getTime());
  lastData = lastDataArray.length > 0 ? lastDataArray[0] : chartData[chartData.length - 1];

  lastUpdatedStr = formatDateTimeSpanish(data.metadata.lastUpdated);
  latestMonth = d3.timeFormat('%B %Y')(lastData.date);

  // Create editorial dek with highlighted values
  const latestPrice = data.metadata.latestValue;
  const { dayChange } = data.metadata.trends;
  const { yearChange } = data.metadata.trends;

  // Handle day change text
  let dayChangeText;
  if (dayChange === 0) {
    dayChangeText = 'unchanged from yesterday';
  } else {
    const dayDirection = dayChange > 0 ? 'más' : 'menos';
    dayChangeText = `<span class="highlight-value">US$ ${formatDecimalES(Math.abs(dayChange))}</span> ${dayDirection} que ayer`;
  }

  // Handle year change text
  let yearChangeText;
  if (yearChange === 0) {
    yearChangeText = 'unchanged from this time a year ago';
  } else {
    const yearDirection = yearChange > 0 ? 'más' : 'menos';
    yearChangeText = `<span class="highlight-value">US$ ${formatDecimalES(Math.abs(yearChange))}</span> ${yearDirection} que en la misma fecha del año pasado`;
  }

  dekText = `El precio promedio nacional de la gasolina es de <span class="highlight-value">US$ ${formatDecimalES(latestPrice)}</span> por galón (3,78 litros), ${dayChangeText} y ${yearChangeText}.`;

  if (dekText && dekText !== '') {
    d3.select('h2.dek')
      .html(dekText);
  }

  draw();
  window.onresize = draw;
});


function draw() {
  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const height = 400;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const xTicks = isMobile ? 2 : 5;
  const yTicks = isMobile ? 5 : 6;

  const xScale = d3.scaleTime()
    .domain([minDomain, maxDomain])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0]);

  // Generate explicit tick values for January 1st of each year
  const yearTicks = [];
  const startYear = minDomain.getFullYear();
  const endYear = maxDomain.getFullYear();

  for (let year = startYear; year <= endYear; year++) {
    // On mobile, only include even years
    if (isMobile && year % 2 !== 0) {
      continue;
    }

    const tickDate = new Date(year, 0, 1); // January 1st
    // Only include if it's within our data range (not at the very edges)
    if (tickDate > minDomain && tickDate < maxDomain) {
      yearTicks.push(tickDate);
    }
  }

  const xAxis = d3.axisBottom()
    .scale(xScale)
    .tickValues(yearTicks)
    .tickFormat(d => d3.timeFormat('%Y')(d));

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .ticks(yTicks)
    .tickSize(-chartWidth)
    .tickFormat(d => `$${d3.format('.2f')(d)}`);

  const path = d3.line()
    .curve(d3.curveLinear)
    .x(d => xScale(d.date))
    .y(d => yScale(d.value));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  // Append zero line
  g.append('line')
    .attr('class', 'zero-line')
    .attr('x1', 0)
    .attr('x2', chartWidth)
    .attr('y1', yScale(minValue))
    .attr('y2', yScale(minValue))
    .attr('stroke', '#393939')
    .attr('stroke-width', 1);

  const line = g.append('g')
    .attr('class', 'line');

  const lineWidth = window.innerWidth < 768 ? 1.5 : 2;

  line.append('path')
    .datum(chartData)
    .attr('stroke', d => '#f18851')
    .attr('stroke-width', lineWidth)
    .attr('fill', 'none')
    .attr('d', path);

  d3.select('.grid.y .domain').remove();

  // Only add annotation if we have valid last data point
  if (lastData && lastData.date && !isNaN(lastData.value)) {
    const annotation = g.append('g')
      .attr('class', 'annotation')
      .attr('transform', `translate(${[xScale(lastData.date), yScale(lastData.value)]})`);

    annotation.append('circle')
      .attr('r', '3')
      .attr('fill', '#f18851')
      .attr('stroke', 'none');

    annotation.append('text')
      .attr('dx', '10px')
      .attr('class', 'annotation-date')
      .text(formatDateSpanish(lastData.date, false));

    annotation.append('text')
      .attr('dx', '10px')
      .attr('class', 'annotation-value')
      .text(`US$ ${formatDecimalES(lastData.value)}`)
      .attr('dy', '1.2em');
  }

  // Append the current date as a timestamp below the chart
  const currentDateString = `Notas: Precio por galón de gasolina regular. Última actualización: ${lastUpdatedStr}.`;

  const timestamp = container.append('div')
    .attr('class', 'footnote')
    .text(currentDateString);

  window.pymChild.sendHeight();
}
